import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  ACADEMY_LEAD,
  InformedSlotSelectionFieldInterface,
  INTENSIVE_LEAD
} from '@nw/shared/config';
import { getParsedDateFormat } from '@nw/shared/utils';

import { OptionsType } from '../../components/types';
import { STRING_DELIMITER } from '../../constants/MiscellaneousConstants';
import { DAY_WISE, WEEK_WISE } from '../../constants/SlotConstants';
import {
  formatDatesStringToNewDateFormat,
  getDateFromDayCode,
  getDayNameByDayCount,
  getLabelText,
  getNextNDates
} from '../../utils/DateTimeUtils';
import {
  getDatesToShowSlotsBasedOnOptionType,
  getSlotsBasedOnSlotConditionsAndDependentFieldValue
} from '../../utils/SlotUtils';
import { getTimeLapsedValues } from '../../utils/TimeLapsedUtils';

import {
  getAllWeekDayToShowSlots,
  getExtraDatesToShowSlots,
  getExtraDatesToShowSlotsOnNextWeek,
  getImmediateSlot,
  getSlotsTimeBasedOnDayCode,
  getSlotTimesBasedOnDate,
  getTimeSlotsBasedOnFormattedOptions
} from './useInformedSlotSelectionFieldUtil';

export const useInformedSlotSelectionField = ({
  values,
  dependentFieldValue,
  academySlotsLanguageOptions,
  intensiveSlotsLanguageOptions,
  slotConditions,
  slotOptions = [],
  isDependent,
  isDateBasedSlots
}: InformedSlotSelectionFieldInterface) => {
  const { t } = useTranslation();

  slotOptions = getSlotsBasedOnSlotConditionsAndDependentFieldValue(
    slotOptions,
    slotConditions,
    dependentFieldValue,
    isDateBasedSlots,
    values
  );

  if (isDateBasedSlots && dependentFieldValue) {
    let formattedOptions = [];
    const slotOptionsBasedOnType = getDatesToShowSlotsBasedOnOptionType(
      getParsedDateFormat(dependentFieldValue),
      DAY_WISE,
      slotOptions,
      isDateBasedSlots
    );

    if (slotOptionsBasedOnType.options.length > 0) {
      formattedOptions.push(slotOptionsBasedOnType);
    }
    return getTimeSlotsBasedOnFormattedOptions(formattedOptions);
  }

  const dayWiseSlotOptions = [];
  const weekWiseSlotOptions = [];

  const extraDatesToShowSlots = getExtraDatesToShowSlots(slotOptions);
  const datesToShowSlotsOnNextWeek =
    getExtraDatesToShowSlotsOnNextWeek(slotOptions);

  const allWeekWiseDayCodeToShowSlots = getAllWeekDayToShowSlots(slotOptions);

  const nextTenDayDates = getNextNDates();

  let allPreferredDatesToShowSlots: Date[] = [];

  nextTenDayDates.forEach((eachDate) => {
    let shouldInclude = false;
    const dayCode = new Date(eachDate).getDay();
    slotOptions.forEach((option) => {
      if (option.slot_hold_dates) {
        const slotHoldDatesArray = formatDatesStringToNewDateFormat(
          option.slot_hold_dates
        );

        if (
          !slotHoldDatesArray.includes(String(eachDate)) &&
          option.preferred_days.includes(dayCode)
        ) {
          shouldInclude = true;
        }
      } else if (option.preferred_days.includes(dayCode)) {
        shouldInclude = true;
      }
    });
    if (shouldInclude) allPreferredDatesToShowSlots.push(eachDate);
  });

  allPreferredDatesToShowSlots = [...allPreferredDatesToShowSlots].splice(0, 2);

  const currentDayDayCode = new Date().getDay();

  const getLabelBasedOnConfig = (dayCode: number, date: Date) => {
    return `${getDayNameByDayCount(dayCode, t)} (${format(date, 'dd-MM-yyyy')})`;
  };

  allWeekWiseDayCodeToShowSlots.forEach((dayCode, index) => {
    if (
      (currentDayDayCode !== 0 && dayCode >= currentDayDayCode) ||
      dayCode === 0
    ) {
      const date = getDateFromDayCode(dayCode);
      const optionsObject = {
        date: date,
        label: getLabelBasedOnConfig(dayCode, date),
        options: getTimeLapsedValues({
          timeLapsedOptions: getSlotsTimeBasedOnDayCode(
            slotOptions,
            dayCode,
            WEEK_WISE,
            date
          ),
          selectedDate: date
        })
      };

      if (optionsObject.options.length) weekWiseSlotOptions.push(optionsObject);
    }
  });

  allPreferredDatesToShowSlots.forEach((eachDate) => {
    const optionsObject = {
      date: eachDate,
      label: getLabelText(eachDate),
      options: getTimeLapsedValues({
        timeLapsedOptions: getSlotsTimeBasedOnDayCode(
          slotOptions,
          new Date(eachDate).getDay(),
          DAY_WISE,
          eachDate
        ),
        selectedDate: eachDate
      })
    };
    if (optionsObject.options.length) dayWiseSlotOptions.push(optionsObject);
  });

  extraDatesToShowSlots.forEach((eachDate) => {
    const dayWiseOptionsObject = {
      date: eachDate,
      label: getLabelText(eachDate),
      options: getTimeLapsedValues({
        timeLapsedOptions: getSlotTimesBasedOnDate(
          slotOptions,
          DAY_WISE,
          eachDate
        ),
        selectedDate: eachDate
      })
    };

    const weekWiseOptionsObject = {
      date: eachDate,
      label: getLabelText(eachDate),
      options: getTimeLapsedValues({
        timeLapsedOptions: getSlotTimesBasedOnDate(
          slotOptions,
          WEEK_WISE,
          eachDate
        ),
        selectedDate: eachDate
      })
    };

    if (dayWiseOptionsObject.options.length)
      dayWiseSlotOptions.push(dayWiseOptionsObject);
    if (weekWiseOptionsObject.options.length)
      weekWiseSlotOptions.push(weekWiseOptionsObject);
  });

  let formattedOptions =
    weekWiseSlotOptions.length > 0 ? weekWiseSlotOptions : dayWiseSlotOptions;

  if (formattedOptions.length === 0) {
    datesToShowSlotsOnNextWeek.forEach((eachDate) => {
      const dayWiseOptionsObject = getDatesToShowSlotsBasedOnOptionType(
        eachDate,
        DAY_WISE,
        slotOptions
      );

      const weekWiseOptionsObject = getDatesToShowSlotsBasedOnOptionType(
        eachDate,
        WEEK_WISE,
        slotOptions
      );

      if (dayWiseOptionsObject.options.length)
        dayWiseSlotOptions.push(dayWiseOptionsObject);
      if (weekWiseOptionsObject.options.length)
        weekWiseSlotOptions.push(weekWiseOptionsObject);
      formattedOptions =
        weekWiseSlotOptions.length > 0
          ? weekWiseSlotOptions
          : dayWiseSlotOptions;
    });
  }

  formattedOptions.forEach((option, index) => {
    const timeSlotOptions: Array<OptionsType> = [];
    const { label } = option;

    if (isDependent && option.options) {
      option.options.forEach((timeSlot) => {
        const { time_value: timeValue, lead_type: leadType } = timeSlot;
        let dependentLanguageOptions;
        if (academySlotsLanguageOptions && leadType === ACADEMY_LEAD) {
          dependentLanguageOptions = academySlotsLanguageOptions;
        } else if (
          intensiveSlotsLanguageOptions &&
          leadType === INTENSIVE_LEAD
        ) {
          dependentLanguageOptions = intensiveSlotsLanguageOptions;
        }

        if (
          dependentLanguageOptions &&
          dependentLanguageOptions[dependentFieldValue]
        ) {
          const dependentLanguageArray =
            dependentLanguageOptions[dependentFieldValue];
          if (dependentLanguageArray.length > 0) {
            dependentLanguageArray.forEach((language) => {
              timeSlotOptions.push({
                value: `${label}${STRING_DELIMITER}${language} - ${timeValue}`,
                label: `${language} - ${timeValue}`
              });
            });
          } else {
            timeSlotOptions.push({
              value: `${label}${STRING_DELIMITER}${timeValue}`,
              label: timeValue
            });
          }
        } else
          timeSlotOptions.push({
            value: `${label}${STRING_DELIMITER}${timeValue}`,
            label: `${timeValue}`
          });
      });
      option.formattedOptions = timeSlotOptions;
    } else {
      try {
        const timeSlotOptions = [];
        option.options.forEach((timeSlot) => {
          const { time_value: timeValue } = timeSlot;
          timeSlotOptions.push({
            value: `${label}${STRING_DELIMITER}${timeValue}`,
            label: timeValue
          });
        });
        option.formattedOptions = timeSlotOptions;
      } catch (err) {
        console.error(err);
      }
    }
  });

  slotOptions.forEach((eachSlot) => {
    if (eachSlot.is_immediate_slot) {
      const immediateSlot = {
        date: new Date(),
        formattedOptions: [
          {
            value: `IMMEDIATE_SLOT${STRING_DELIMITER}${getImmediateSlot()}`,
            label: eachSlot.time_value
          }
        ],
        label: eachSlot.label,
        options: [eachSlot]
      };

      formattedOptions = [immediateSlot, ...formattedOptions];
    }
  });

  return formattedOptions;
};
